import type { PublicNextStep } from "./types/public-education.types"

// One key, as the brief specifies (`sessionStorage["pe-session"]`) — its
// value is a small JSON envelope carrying `next` alongside the session
// itself, not just the bare token. A reload has no fragment to re-resolve,
// so it needs to know without another network round trip whether the stored
// session already passed verification (`next: "guide"`) or is still waiting
// on it (`next: "verify"`) — `verify/check`'s own 200 carries only `{session}`,
// so this module is what remembers "and now it's verified" across a reload.
const STORAGE_KEY = "pe-session"

export interface StoredEducationSession {
  session: string
  next: PublicNextStep
}

export const readStoredEducationSession = (): StoredEducationSession | null => {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<StoredEducationSession>
    if (typeof parsed.session !== "string" || !parsed.session) return null
    return {
      session: parsed.session,
      next: parsed.next === "verify" ? "verify" : "guide"
    }
  } catch {
    return null
  }
}

export const writeStoredEducationSession = (
  value: StoredEducationSession
): void => {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value))
  } catch {
    // Without storage the page still works for this one load; only a reload
    // loses the session and has to fail as "unavailable" (no fragment left
    // to re-resolve from).
  }
}

export const clearStoredEducationSession = (): void => {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY)
  } catch {
    // Nothing to clean up if storage never worked in the first place.
  }
}
